import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedRoom } from "redux/actions";
import { Container, DefaultIconContainer, Border } from "./styles";
import colors from "constants/colors";
import {
  UpArrowIcon,
  DownArrowIcon,
  StairwellIcon,
  LoungeIcon,
  LaundryIcon,
  SaunaRoomIcon,
  FemaleIcon,
  MaleIcon,
  GymIcon,
  OutOfServiceIcon,
  KingIcon,
  QueenIcon,
  KnightIcon,
  RookIcon,
  PawnIcon,
  WheelchairIcon,
} from "assets/icons";

const Room = ({
  width,
  height,
  roomData = {},
  disabled,
  selected,
  className,
  roomName,
  floorBackgroundColor,
  roomNumber,
  wheelchairAccessible,
}) => {
  const dispatch = useDispatch();
  const currentRoom = useSelector((state) => state.selectReducer.currentRoom);
  const [needsFix, setNeedsFix] = useState(false);

  useEffect(() => {
    // Detect WebKit browsers (Safari + Chrome on iOS)
    const isWebKit =
      /AppleWebKit/.test(navigator.userAgent) &&
      !/Chrome\/(?!.*Mobile)/.test(navigator.userAgent);

    // Alternative: Test for iOS rendering behavior
    const isIOSChrome = /CriOS/.test(navigator.userAgent);
    const isIOSFirefox = /FxiOS/.test(navigator.userAgent);
    const isIOSWebView = /WebKit.*Mobile/.test(navigator.userAgent);

    setNeedsFix(isWebKit || isIOSChrome || isIOSFirefox || isIOSWebView);
  }, []);

  const { roomStatusHistories, roomType } = roomData;
  const status = roomStatusHistories ? roomStatusHistories?.[0]?.status : null;

  let backgroundColor;
  switch (status) {
    case "DIRTY":
      backgroundColor = colors.yellow;
      break;
    case "CLEAN":
      backgroundColor = colors.lightGreen;
      break;
    case "INSPECTED":
      backgroundColor = colors.blue;
      break;
    case "OUT_OF_ORDER":
      backgroundColor = colors.darkGrey;
      break;
    case undefined:
      backgroundColor = colors.darkGrey;
      break;
    default:
      backgroundColor = colors.darkGrey;
  }
  if(disabled) {
    backgroundColor = colors.silver;
  }

  const colorReverse = !(
    backgroundColor === colors.blue || backgroundColor === colors.darkGrey
  );

  const RoomDefaultIcon = () => {
    if (status === "OUT_OF_ORDER") {
      return (
        <DefaultIconContainer
          width="15px"
          height="15px"
          colorReverse={colorReverse}
        >
          <OutOfServiceIcon className="icon" />
        </DefaultIconContainer>
      );
    } else if (roomName?.length > 0) {
      switch (roomName) {
        case "Stairwell":
          return (
            <DefaultIconContainer
              width="20px"
              height="20px"
              colorReverse={colorReverse}
            >
              <StairwellIcon className="icon" />
            </DefaultIconContainer>
          );
        case "Lounge":
          return (
            <DefaultIconContainer
              width="25px"
              height="25px"
              colorReverse={colorReverse}
            >
              <LoungeIcon className="icon" />
            </DefaultIconContainer>
          );
        case "Laundry":
          return (
            <DefaultIconContainer
              width="20px"
              height="20px"
              colorReverse={colorReverse}
            >
              <LaundryIcon className="icon" />
            </DefaultIconContainer>
          );
        case "Steam Room":
        case "Sauna":
          return (
            <DefaultIconContainer
              width="15px"
              height="15px"
              colorReverse={colorReverse}
            >
              <SaunaRoomIcon className="icon" />
            </DefaultIconContainer>
          );
        case "Women's Changeroom":
        case "Men's Changeroom":
        case "Gym":
          return (
            <DefaultIconContainer
              width="30px"
              height="30px"
              colorReverse={colorReverse}
            >
              {roomName === "Women's Changeroom" ? (
                <FemaleIcon className="icon" />
              ) : roomName === "Men's Changeroom" ? (
                <MaleIcon className="icon" />
              ) : roomName === "Gym" ? (
                <GymIcon className="icon" />
              ) : null}
            </DefaultIconContainer>
          );
        case "LeftElevator":
        case "RightElevator":
          return (
            <DefaultIconContainer
              width="10px"
              height="10px"
              colorReverse={colorReverse}
            >
              {roomName === "LeftElevator" ? (
                <UpArrowIcon className="icon" />
              ) : (
                <DownArrowIcon className="icon" />
              )}
            </DefaultIconContainer>
          );
        default:
          return null;
      }
    } else {
      switch (roomType?.name) {
        case "Stairwell":
          return (
            <DefaultIconContainer
              width="20px"
              height="20px"
              colorReverse={colorReverse}
            >
              <StairwellIcon className="icon" />
            </DefaultIconContainer>
          );
        case "Lounge":
          return (
            <DefaultIconContainer
              width="25px"
              height="25px"
              colorReverse={colorReverse}
            >
              <LoungeIcon className="icon" />
            </DefaultIconContainer>
          );
        case "King":
          return (
            <DefaultIconContainer
              width="15px"
              height="15px"
              colorReverse={colorReverse}
            >
              <KingIcon className="icon" />
            </DefaultIconContainer>
          );
        case "Queen":
          return (
            <DefaultIconContainer
              width="15px"
              height="15px"
              colorReverse={colorReverse}
            >
              <QueenIcon className="icon" />
            </DefaultIconContainer>
          );
        case "Knight":
        case "Knight Inside":
          return (
            <DefaultIconContainer
              width="15px"
              height="15px"
              colorReverse={colorReverse}
            >
              <KnightIcon className="icon" />
            </DefaultIconContainer>
          );
        case "Rook":
        case "Rook Inside":
          return (
            <DefaultIconContainer
              width="15px"
              height="15px"
              colorReverse={colorReverse}
            >
              <RookIcon className="icon" />
            </DefaultIconContainer>
          );
        case "Pawn":
        case "Pawn Inside":
        case "Bishop Inside":
          return (
            <DefaultIconContainer
              width="15px"
              height="15px"
              colorReverse={colorReverse}
            >
              <PawnIcon className="icon" />
            </DefaultIconContainer>
          );
        default:
          return null;
      }
    }
  };

  if (currentRoom && currentRoom?.roomNumber === roomNumber) {
    backgroundColor = colors.white;
  }

  return (
    <>
      <Container
        className={className}
        roomName={roomName}
        selected={selected}
        needsFix={needsFix}
        colorReverse={colorReverse}
        roomNumber={roomNumber}
        disabled={
          disabled ||
          (currentRoom && currentRoom?.roomNumber === roomNumber) ||
          roomType?.name === "Amenities" ||
          roomName === "Lounge" ||
          roomName === "Laundry"
        }
        backgroundColor={backgroundColor}
        floorBackgroundColor={floorBackgroundColor}
        width={width}
        height={height}
        wheelchairAccessible={wheelchairAccessible}
        onClick={() => {
          if (
            disabled ||
            (currentRoom && currentRoom?.roomNumber === roomNumber) ||
            roomType?.name === "Amenities" ||
            roomName === "Lounge" ||
            roomName === "Laundry"
          )
            return;
          dispatch(setSelectedRoom(roomData));
        }}
      >
        <div className="roomContent">
          {roomName ? null : <div className="roomNumber">{roomNumber}</div>}
          {RoomDefaultIcon()}
          {wheelchairAccessible ? (
            <WheelchairIcon
              className="wheelchairIcon"
              colorReverse={colorReverse}
            />
          ) : null}
        </div>
      </Container>
      {selected ? <Border roomNumber={roomNumber} /> : null}
    </>
  );
};

export default Room;
